import React, { ReactNode, useState } from 'react';
import { cn } from '@/lib/utils';

interface TouchableCardProps {
  children: ReactNode;
  onClick?: () => void;
  className?: string;
  rippleColor?: 'light' | 'dark' | 'primary';
  disabled?: boolean;
}

const TouchableCard: React.FC<TouchableCardProps> = ({
  children,
  onClick,
  className,
  rippleColor = 'light',
  disabled = false,
}) => {
  const [ripples, setRipples] = useState<{id: number; x: number; y: number; size: number}[]>([]);
  let rippleTimeout: ReturnType<typeof setTimeout>;
  let rippleCount = 0;

  // Map ripple color to tailwind classes
  const rippleClasses = {
    light: 'bg-white/40',
    dark: 'bg-black/20',
    primary: 'bg-primary/30',
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return;
    
    // Get position relative to the button
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Determine size based on the larger dimension of the button
    const size = Math.max(rect.width, rect.height) * 2;
    
    // Add new ripple
    const newRipple = { id: rippleCount++, x, y, size };
    setRipples(prev => [...prev, newRipple]);
    
    // Remove ripple after animation
    rippleTimeout = setTimeout(() => {
      setRipples(prev => prev.filter(r => r.id !== newRipple.id));
    }, 500);

    if (onClick) onClick();
  };

  // Clean up timeout when component unmounts
  React.useEffect(() => {
    return () => {
      if (rippleTimeout) clearTimeout(rippleTimeout);
    };
  }, []);

  return (
    <div 
      className={cn(
        "relative overflow-hidden touch-ripple-card",
        disabled ? "opacity-70 pointer-events-none" : "cursor-pointer active:scale-[0.98] transition-transform",
        className
      )}
      onClick={handleClick}
    >
      {children}

      {/* Ripple elements */}
      {ripples.map(ripple => (
        <span 
          key={ripple.id}
          className={cn(
            "absolute rounded-full animate-ripple-effect",
            rippleClasses[rippleColor]
          )}
          style={{
            left: ripple.x - ripple.size / 2,
            top: ripple.y - ripple.size / 2,
            width: ripple.size,
            height: ripple.size,
          }}
        />
      ))}
    </div>
  );
};

export default TouchableCard; 